import * as THREE from 'three';
import {CabinetMesh, Faces} from 'components/customer/RoomPlanner/types';
import type {getDirection} from 'components/customer/RoomPlanner/helpers/calculations';

/**
 * What every snapping algorithm is handed besides the two products: the drag
 * and the scene around it. Built once per handleSnapping call.
 */
export interface SnapContext {
    targetPos: THREE.Vector3;
    // Where the dragged product is now, before this move.
    currentPos: THREE.Vector3;
    productsRef: THREE.Group[];
    overlappingCabinets: CabinetMesh[] | null;
    camera: THREE.Camera;
    wallGroup?: THREE.Object3D | null;
    latestWidth: number;
    latestDepth: number;
    latestHeight: number;
}

/**
 * What the router and the strategies need to know about one product, worked
 * out once per drag for the dragged product and once per pair for the other.
 */
export interface ProductFacts {
    group: THREE.Group;
    // World box at the product's current position. Shared, so read only.
    box: THREE.Box3;
    height: number;
    direction: ReturnType<typeof getDirection>;
    isUpper: boolean;
    isBulkhead: boolean;
    // A kicker frame.
    isFloorProduct: boolean;
    isKickLength: boolean;
    // The flag getSnappingData has always used for kick-length panels; it
    // differs from isKickLength on products that set only one of the two.
    isNonManualPanel: boolean;
    // A manual panel, panel, vermin strip or filler.
    isFiller: boolean;
    sitsOnFloor: boolean;
    hasLegs: boolean;
    isCornerLShape: boolean;
    // An L, key or U shaped panel.
    isNonRectShape: boolean;
    isReturn: boolean;
    isRightReturn: boolean;
    ignoresSnapping: boolean;
    // An appliance GLB. Pairs that include one use placeholder snapping.
    isPlaceholder: boolean;
}

/**
 * The two products one routing decision is about.
 */
export interface PairFacts {
    selected: ProductFacts;
    other: ProductFacts;
    // The tops are within 5 units of each other.
    hasMatchingTopY: boolean;
    sameDirection: boolean;
}

/**
 * What a snap does to the dragged product's attachment, applied by
 * applySnapState (snapping/state.ts):
 * - attached: held in snap mode, and remembered as snapped for the detach
 *   ghost.
 * - detached: out of snap mode, and no longer remembered as snapped.
 * - suspended: out of snap mode for now, still remembered as snapped, so a
 *   drag that loses its snap still shows the detach ghost.
 */
export type SnapMode = 'attached' | 'detached' | 'suspended';

/**
 * Attachment state a snap leaves the dragged product in: its mode, the box it
 * may slide within while attached, and which face of the other product it
 * attached to. A drag applies one: the winning candidate's, or a rejection's
 * when no winner carries one.
 */
export interface SnapState {
    mode?: SnapMode;
    bounds?: number[];
    snappedSide?: string;
}

/**
 * A proposed position and the distance it was chosen by, so a later
 * comparison is always against the distance of the position actually held.
 */
export interface SnapCandidate {
    position: THREE.Vector3;
    distance: number;
    snapState?: SnapState;
}

/**
 * A strategy's veto: this pair must not snap. The search goes on to the other
 * products, and the rejection's snap state is the drag's only if none of them
 * wins with one of its own.
 */
export interface SnapRejection {
    rejected: true;
    snapState: SnapState;
}

/**
 * What a strategy says about one pair: a candidate, nothing in range (null),
 * or a rejection.
 */
export type SnapOutcome = SnapCandidate | SnapRejection | null;

export const isSnapRejection = (
    outcome: SnapOutcome
): outcome is SnapRejection =>
    outcome !== null && 'rejected' in outcome && outcome.rejected;

export const isSnapCandidate = (
    outcome: SnapOutcome
): outcome is SnapCandidate => outcome !== null && !isSnapRejection(outcome);

/**
 * Every snapping algorithm: the dragged product, one other product, the
 * context and what the router already knows about the pair in, an outcome
 * out. Changes nothing outside itself.
 */
export type SnapStrategy = (
    cabinetGroup: THREE.Group,
    otherProduct: THREE.Group,
    ctx: SnapContext,
    pair: PairFacts
) => SnapOutcome;

export const SnapDir = {
    Right: 0,
    Left: 1,
    Front: 2,
    Back: 3,
} as const;

export type SnapDirValue = (typeof SnapDir)[keyof typeof SnapDir];

export interface ComputeCabinetSnapMetaInput {
    cabinetGroup: THREE.Group;
    otherProduct: THREE.Group;
    snappedProduct: {depth: number; width: number};
}

export interface ComputeCabinetSnapMetaResult {
    exteriorThickness: number;
    otherProductExtThickness: number;
    carcaseThickness: number;
    otherCabinetDepth: number;
    otherCabinetWidth: number;
    kickerFrameEdge: Faces;
    otherProductKickerFrameEdge: Faces;
}

export interface CabinetRotationFlags {
    isLeft: boolean;
    isRight: boolean;
    isBack: boolean;
    isNormal: boolean;
}

export interface ComputeSnapContextInput {
    cabinetGroup: THREE.Group;
    otherProduct: THREE.Group;
    cabinetRotations: {
        selected: CabinetRotationFlags;
        other: CabinetRotationFlags;
    };
    isOtherCornerAndLShapedCabinet: boolean;
    isKickLength: boolean;
}

export interface ComputeSnapContextResult {
    isBackToBack: boolean;
    backToBackData: {isFrontAndBack: boolean};
    isFacingRightAndSnapToLShaped: boolean;
    isFacingLeftAndSnapToRightReturn: boolean;
    allowDirections: SnapDirValue[];
    isUpperSnappingToTall: boolean;
    isUpperSnappingToAnotherUpper: boolean;
    shouldSnapToRightReturnProduct: boolean;
    shouldSnapParallelReturnSides: boolean;
    snappedProduct: {width: number; depth: number; height: number};
    isPanelProduct: boolean;
    isPanelProductOther: boolean;
    isFillerProduct: boolean;
    isFloorProduct: boolean;
    isBulkheadSnapping: boolean;
    isFloorProductSnapping: boolean;
}

export interface ComputeDistancePosInput {
    targetPos: THREE.Vector3;
    isBackToBack: boolean;
    backToBackData: {isFrontAndBack: boolean};
    cabinetRotations: {
        selected: CabinetRotationFlags;
        other: CabinetRotationFlags;
    };
    currentSnappingDir: SnapDirValue;
    snappedProduct: {width: number; depth: number; height: number};
    latestWidth: number;
    latestDepth: number;
    latestHeight: number;
    isKickLength: boolean;
    isFillerProduct: boolean;
    isFloorProductSnapping: boolean;
    isSelectedCornerAndLShapedCabinet: boolean;
    isOtherCornerAndLShapedCabinet: boolean;
    productSize: number;
    otherProduct: THREE.Group;
    isBulkheadSnapping: boolean;
    isUpperSnappingToTall: boolean;
    isUpperSnappingToAnotherUpper: boolean;
    shouldSnapToRightReturnProduct: boolean;
    shouldSnapParallelReturnSides: boolean;
}

export interface ApplyNonBackToBackPositioningInput {
    proposedPos: THREE.Vector3;
    cabinetGroup: THREE.Group;
    otherProduct: THREE.Group;
    cabinetRotations: {
        selected: CabinetRotationFlags;
        other: CabinetRotationFlags;
    };
    currentSnappingDir: SnapDirValue;
    isFillerProduct: boolean;
    shouldSnapToRightReturnProduct: boolean;
    shouldSnapParallelReturnSides: boolean;
    isBulkheadSnapping: boolean;
    isFloorProductSnapping: boolean;
    isFloorProduct: boolean;
    isPanelProduct: boolean;
    isPanelProductOther: boolean;
    exteriorThickness: number;
    otherProductExtThickness: number;
    carcaseThickness: number;
    snappedProduct: {width: number; depth: number};
    otherCabinetDepth: number;
    otherCabinetWidth: number;
    kickerFrameEdge: Faces;
    otherProductKickerFrameEdge: Faces;
    hasOverlappingKickerFrame: boolean;
    latestWidth: number;
    latestDepth: number;
    latestHeight: number;
    isFacingRightAndSnapToLShaped: boolean;
    isFacingLeftAndSnapToRightReturn: boolean;
    isKickLength: boolean;
    selectedCabinetDirection: string;
    otherCabinetDirection: string;
    allowDirections: SnapDirValue[];
    snapped: boolean;
}

export interface ApplyBackToBackPositioningInput {
    proposedPos: THREE.Vector3;
    cabinetGroup: THREE.Group;
    backToBackData: {isFrontAndBack: boolean};
    cabinetRotations: {
        selected: CabinetRotationFlags;
        other: CabinetRotationFlags;
    };
    currentSnappingDir: SnapDirValue;
    exteriorThickness: number;
    carcaseThickness: number;
    latestWidth: number;
    latestDepth: number;
    kickerFrameEdge: Faces;
    otherProductKickerFrameEdge: Faces;
    snappedProduct: {width: number; depth: number};
    otherCabinetWidth: number;
    isFloorProductSnapping: boolean;
    allowDirections: SnapDirValue[];
    snapped: boolean;
}

export interface ComputeSnapDistancesInput {
    proposedPos: THREE.Vector3;
    distancePos: THREE.Vector3;
    kickerFrontDistancePos: THREE.Vector3;
    kickerFrontRightDistancePos: THREE.Vector3;
    targetPos: THREE.Vector3;
    isKickLength: boolean;
    selectedCabinetDirection: string;
    currentSnappingDir: SnapDirValue;
}
